import {
    Close,
    ConfirmationNumber,
    DescriptionOutlined,
    GridView,
    Search,
    TableRows,
} from '@mui/icons-material';
import {
    Badge,
    Box,
    Chip,
    IconButton,
    InputAdornment,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from '@mui/material';

import DocumentStatusFilter from './documentStatusFilter';

import { TICKET_STATUS_CARD_STYLES, TicketStatus } from 'data/constants/constants';
import { documentsTicketsToggleKey, isTicketsFilterMode } from 'helpers/documentTickets';
import useTranslate from 'hooks/useTranslate';
import { getTicketStatusLabelKey } from 'utils/ticket';

export type ViewMode = 'card' | 'table';

/** Which filter category is active — only one at a time (OR condition). */
export type FilterMode = 'all' | 'status' | 'tickets';

interface TicketStatusCount {
    status: TicketStatus;
    count: number;
}

interface DocumentToolbarProps {
    search: string;
    onSearchChange: (value: string) => void;
    filterMode: FilterMode;
    statusFilter: string | null;
    onStatusFilterChange: (status: string | null) => void;
    ticketStatusFilter: TicketStatus | null;
    onTicketStatusFilterChange: (status: TicketStatus | null) => void;
    onFilterModeChange: (mode: FilterMode) => void;
    viewMode: ViewMode;
    onViewModeChange: (mode: ViewMode) => void;
    ticketStatusCounts: TicketStatusCount[];
}

const ALL_TICKET_STATUSES = [
    TicketStatus.PENDING,
    TicketStatus.IN_REVIEW,
    TicketStatus.RESOLVED,
    TicketStatus.CLOSED,
];

const DocumentToolbar = ({
    search,
    onSearchChange,
    filterMode,
    statusFilter,
    onStatusFilterChange,
    ticketStatusFilter,
    onTicketStatusFilterChange,
    onFilterModeChange,
    viewMode,
    onViewModeChange,
    ticketStatusCounts,
}: DocumentToolbarProps) => {
    const { translate } = useTranslate();
    const ticketCountMap = new Map(ticketStatusCounts.map(({ status, count }) => [status, count]));
    const totalTicketCount = ticketStatusCounts.reduce((sum, { count }) => sum + count, 0);

    return (
        <Stack spacing={1.5}>
            <Stack
                direction="row"
                spacing={1}
                useFlexGap
                sx={{ alignItems: 'center', flexWrap: 'wrap' }}
            >
                <TextField
                    size="small"
                    placeholder={translate(
                        isTicketsFilterMode(filterMode) ? 'searchTickets' : 'searchDocuments'
                    )}
                    value={search}
                    onChange={(event) => onSearchChange(event.target.value)}
                    sx={{ width: { xs: '100%', sm: 240 } }}
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search fontSize="small" color="action" />
                                </InputAdornment>
                            ),
                            ...(search && {
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            size="small"
                                            onClick={() => onSearchChange('')}
                                            edge="end"
                                            aria-label={translate('clearAll')}
                                        >
                                            <Close sx={{ fontSize: 16 }} />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }),
                        },
                    }}
                />

                {isTicketsFilterMode(filterMode) ? null : (
                    <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={viewMode}
                        onChange={(_, value) => value && onViewModeChange(value)}
                    >
                        <ToggleButton value="card">
                            <Tooltip title={translate('cardView')}>
                                <GridView sx={{ fontSize: 18 }} />
                            </Tooltip>
                        </ToggleButton>
                        <ToggleButton value="table">
                            <Tooltip title={translate('tableView')}>
                                <TableRows sx={{ fontSize: 18 }} />
                            </Tooltip>
                        </ToggleButton>
                    </ToggleButtonGroup>
                )}

                <Stack
                    direction="row"
                    spacing={0.5}
                    useFlexGap
                    sx={{
                        flex: 1,
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                    }}
                >
                    {viewMode === 'card' && !isTicketsFilterMode(filterMode) ? (
                        <DocumentStatusFilter
                            value={statusFilter}
                            onChange={onStatusFilterChange}
                        />
                    ) : null}
                    <Badge badgeContent={totalTicketCount} color="error" max={999}>
                        <Chip
                            icon={
                                isTicketsFilterMode(filterMode) ? (
                                    <DescriptionOutlined sx={{ fontSize: 14 }} />
                                ) : (
                                    <ConfirmationNumber sx={{ fontSize: 14 }} />
                                )
                            }
                            label={translate(documentsTicketsToggleKey(filterMode))}
                            variant={isTicketsFilterMode(filterMode) ? 'filled' : 'outlined'}
                            color={isTicketsFilterMode(filterMode) ? 'warning' : 'default'}
                            onClick={() =>
                                onFilterModeChange(
                                    isTicketsFilterMode(filterMode) ? 'all' : 'tickets'
                                )
                            }
                            sx={{
                                width: 148,
                                fontWeight: 600,
                                justifyContent: 'center',
                                '& .MuiChip-label': { px: 0.75 },
                            }}
                        />
                    </Badge>
                </Stack>
            </Stack>

            {isTicketsFilterMode(filterMode) ? (
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                            xs: 'repeat(2, 1fr)',
                            sm: 'repeat(4, 1fr)',
                        },
                        gap: 1.5,
                    }}
                >
                    {ALL_TICKET_STATUSES.map((status) => {
                        const count = ticketCountMap.get(status) ?? 0;
                        const style = TICKET_STATUS_CARD_STYLES[status];
                        const isActive = ticketStatusFilter === status;

                        return (
                            <Box
                                key={status}
                                onClick={() => onTicketStatusFilterChange(isActive ? null : status)}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    py: 1.5,
                                    px: 2,
                                    borderRadius: 1.5,
                                    cursor: 'pointer',
                                    bgcolor: isActive ? style.bg : 'grey.50',
                                    border: 1,
                                    borderColor: isActive ? style.border : 'divider',
                                    '&:hover': { bgcolor: style.bg },
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: 22,
                                        fontWeight: 700,
                                        color: style.color,
                                        lineHeight: 1.2,
                                    }}
                                >
                                    {count}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: 12,
                                        fontWeight: 600,
                                        color: style.color,
                                        mt: 0.25,
                                    }}
                                >
                                    {translate(getTicketStatusLabelKey(status))}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>
            ) : null}
        </Stack>
    );
};

export default DocumentToolbar;
